"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MessageSquare, SquarePen, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FluidOrb } from "@/components/ui/fluid-orb";
import { AccessDeniedCard } from "@/components/shared/access-denied-card";
import { cn } from "@/lib/utils";
import { isCommandPanelDisabledRoute } from "@/utils/command-panel-routes";
import { useCommandPanel } from "./command-panel-provider";
import { CommandPanelThread } from "./command-panel-thread";
import { CommandPanelHistory } from "./command-panel-history";

export function CommandPanel() {
    const { isOpen, close, activeAgent, isLoadingActiveAgent, canListAgents, thread } = useCommandPanel();
    const [tab, setTab] = useState<"chat" | "history">("chat");
    const pathname = usePathname();

    // Command Team already has its own native chat sidebar, and auth/super-admin
    // pages have no session to back the panel — never surface it on any of
    // these, even if it was left open while on another page.
    const isCommandTeamRoute = pathname?.startsWith("/teams/command-team") ?? false;

    if (!isOpen || isCommandTeamRoute || isCommandPanelDisabledRoute(pathname)) return null;

    const hasThread = canListAgents && !isLoadingActiveAgent && Boolean(activeAgent);

    return (
        <aside className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l bg-background shadow-xl animate-in slide-in-from-right duration-300 sm:w-[400px]">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
                {hasThread ? (
                    <div className="flex items-center rounded-full bg-muted p-1">
                        {(["chat", "history"] as const).map((t) => (
                            <button
                                key={t}
                                type="button"
                                onClick={() => setTab(t)}
                                className={cn(
                                    "rounded-full px-3.5 py-1.5 text-sm capitalize transition-colors",
                                    tab === t
                                        ? "bg-background font-semibold shadow-sm"
                                        : "font-medium text-muted-foreground hover:text-foreground"
                                )}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="truncate px-2 text-sm font-semibold">
                        {activeAgent ? activeAgent.name : "Talk to Command"}
                    </p>
                )}
                <div className="flex items-center gap-1">
                    {hasThread ? (
                        <button
                            type="button"
                            onClick={() => {
                                thread.startNewChat();
                                setTab("chat");
                            }}
                            aria-label="New chat"
                            className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <SquarePen className="size-4" />
                        </button>
                    ) : null}
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Close Command"
                        className="flex size-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        <X className="size-4" />
                    </button>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {!canListAgents ? (
                    <div className="p-4">
                        <AccessDeniedCard message="You don't have permission to view Command agents. Contact your administrator if you need access." />
                    </div>
                ) : isLoadingActiveAgent ? (
                    <div className="flex h-full items-center justify-center">
                        <FluidOrb size="md" speed="fast" />
                    </div>
                ) : !activeAgent ? (
                    <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
                        <div className="flex size-16 items-center justify-center rounded-full bg-muted">
                            <MessageSquare className="size-8 text-muted-foreground" />
                        </div>
                        <div className="space-y-1">
                            <h2 className="text-lg font-semibold">No active agents found</h2>
                            <p className="max-w-sm text-sm text-muted-foreground">
                                Create or activate an agent to start chatting.
                            </p>
                        </div>
                        <Button asChild variant="outline" size="sm" onClick={close}>
                            <Link href="/teams/command-team/agents">View agents</Link>
                        </Button>
                    </div>
                ) : tab === "chat" ? (
                    <CommandPanelThread />
                ) : (
                    <CommandPanelHistory
                        onSelectSession={(sessionId) => {
                            thread.selectSession(sessionId);
                            setTab("chat");
                        }}
                    />
                )}
            </div>
        </aside>
    );
}
